"use client";
import Login from "@/src/components/login/login";
import { Button } from "@/src/components/ui/button";
import Loading from "@/src/components/ui/loading";
import { LanguageSwitcher } from "@/src/components/ui/language-switcher";
import { useToast } from "@/src/hooks/use-toast";
import { ArrowRight, CheckCircle2, Moon, ShieldCheck, Sparkles, Sun } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { useRouter } from "@/src/i18n/routing";
import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import { fetchDashboardLayoutAction } from "./action";
import { ApplicationContextState } from "../dashboard/workspace/types/workspace";
import { saveToLocalStorage } from "../dashboard/shared/saveToLocalStorage";

interface LoginPageClientProps {
  /** Layout prefetchado por el Server Component (null si el usuario no tenía sesión al renderizar). */
  prefetchedLayout: ApplicationContextState | null;
}

export default function LoginPageClient({
  prefetchedLayout,
}: LoginPageClientProps) {
  const t = useTranslations("Login");
  const tema = useTranslations();
  const { toast } = useToast();
  const { resolvedTheme, setTheme } = useTheme();
  const [timeToRedirect, setTimeToRedirect] = useState(5);
  const { data: session, status } = useSession();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const handleThemeChange = () => {
    toast({
      title: tema("Theme.title"),
      description:
        tema("Theme.changed") +
        (resolvedTheme === "dark"
          ? ` ${tema("Theme.toLight")}`
          : ` ${tema("Theme.toDark")}`),
    });
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  // Persiste el layout en localStorage en cuanto el usuario está autenticado.
  // Si el Server Component ya prefetchó (usuario tenía sesión), lo usa directamente.
  // Si es un login fresco, llama el Server Action para obtenerlo.
  useEffect(() => {
    if (status !== "authenticated") return;

    async function persistLayout() {
      try {
        const application =
          prefetchedLayout ?? (await fetchDashboardLayoutAction());
        if (application) {
          saveToLocalStorage(application);
          setTheme(application.preferences.theme);
        } else {
          localStorage.removeItem("dashboardLayout"); // forzar fetch fresco en el dashboard
        }
      } catch {
        // Fallo silencioso — el dashboard cargará normalmente
      }
    }

    persistLayout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Countdown y redirect automático
  useEffect(() => {
    if (status !== "authenticated") return;

    const redirectTimeout = setTimeout(() => {
      router.push("/dashboard");
    }, 5000);

    const countdownInterval = setInterval(() => {
      setTimeToRedirect((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearTimeout(redirectTimeout);
      clearInterval(countdownInterval);
    };
  }, [status, router]);

  const [blockRedirect, setBlockRedirect] = useState(false);
  const handleRedirectNow = () => {
    setBlockRedirect(true);
    router.push("/dashboard");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,rgba(20,184,166,0.22),transparent_32%),radial-gradient(circle_at_90%_85%,rgba(14,165,233,0.18),transparent_30%)]" />
      <div className="absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-teal-500/10 blur-3xl" />
      <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-sky-500/10 blur-3xl" />
      <div className="absolute right-5 top-5 z-50 flex gap-2">
        <LanguageSwitcher />
        <Button type="button" variant="secondary" size="icon" onClick={handleThemeChange} aria-label="Cambiar tema" className="relative z-50 border border-white/15 bg-white/10 text-white hover:bg-white/20">
          <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
        </Button>
      </div>
      {status === "loading" || isLoading ? (
        <Loading />
      ) : status === "authenticated" ? (
        <div className="relative z-10 flex min-h-screen items-center justify-center p-6">
          <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.08] p-8 text-white shadow-2xl backdrop-blur-xl sm:p-10">
            <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-400/15 text-emerald-300">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h1 className="text-2xl font-semibold">{t("alreadyAuthenticated")}</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              {t("redirectingToDashboard")} {timeToRedirect} {t("seconds")} ...
            </p>
            <Button
              className="mt-7 h-11 w-full rounded-xl bg-white text-slate-950 hover:bg-slate-100"
              onClick={handleRedirectNow}
              disabled={blockRedirect}
            >
              {t("redirectNow")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="relative z-10 flex min-h-screen w-full items-center justify-center gap-20 p-6 lg:justify-between lg:px-20 xl:px-32">
          <div className="hidden max-w-xl text-white lg:block">
            <div className="mb-8 flex items-center gap-3 text-teal-300">
              <ShieldCheck className="h-7 w-7" />
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">Cloud Tek</span>
            </div>
            <h2 className="text-5xl font-semibold leading-[1.08] tracking-tight">
              {t("title")}<br />
              <span className="text-teal-300">UGAT Platform</span>
            </h2>
            <div className="mt-8 flex items-center gap-2 text-sm text-slate-400">
              <Sparkles className="h-4 w-4 text-teal-300" />
              Secure access to your operational workspace
            </div>
          </div>
          <Login isLoading={isLoading} setIsLoading={setIsLoading} />
        </div>
      )}
    </div>
  );
}
